import React from 'react';
import { Brain, TrendingUp, TrendingDown, Minus, Shield, AlertTriangle, Zap } from 'lucide-react';
import { AIInsight } from '../types';

interface AIInsightPanelProps {
  insight: AIInsight;
  symbol: string;
}

const AIInsightPanel: React.FC<AIInsightPanelProps> = ({ insight, symbol }) => {
  const getSentimentIcon = () => {
    switch (insight.sentiment) {
      case 'bullish':
        return <TrendingUp className="text-neon-green" size={24} />;
      case 'bearish':
        return <TrendingDown className="text-neon-red" size={24} />;
      default:
        return <Minus className="text-terminal-muted" size={24} />;
    }
  };

  const getSentimentColor = () => {
    switch (insight.sentiment) {
      case 'bullish':
        return 'text-neon-green';
      case 'bearish':
        return 'text-neon-red';
      default:
        return 'text-terminal-muted';
    }
  };

  const getRecommendationStyle = () => {
    switch (insight.recommendation) {
      case 'BUY':
        return 'bg-neon-green/20 text-neon-green border-neon-green/50';
      case 'SELL':
        return 'bg-neon-red/20 text-neon-red border-neon-red/50';
      default:
        return 'bg-neon-yellow/20 text-neon-yellow border-neon-yellow/50';
    }
  };

  const getRiskIcon = () => {
    switch (insight.riskLevel) {
      case 'LOW':
        return <Shield className="text-neon-green" size={16} />;
      case 'HIGH':
        return <AlertTriangle className="text-neon-red" size={16} />;
      default:
        return <Zap className="text-neon-yellow" size={16} />;
    }
  };

  const getRiskColor = () => {
    switch (insight.riskLevel) {
      case 'LOW':
        return 'text-neon-green';
      case 'HIGH':
        return 'text-neon-red';
      default:
        return 'text-neon-yellow';
    }
  };

  // Create circular progress for confidence
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (insight.confidence / 100) * circumference;

  return (
    <div className="terminal-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-neon-blue/20 rounded-lg">
          <Brain className="text-neon-blue" size={24} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-terminal-text">AI Analysis</h3>
          <p className="text-sm text-terminal-muted">Real-time sentiment & recommendation</p>
        </div>
      </div>

      {/* Sentiment Gauge */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-terminal-text">Market Sentiment</span>
          <div className="flex items-center gap-2">
            {getSentimentIcon()}
            <span className={`font-semibold capitalize ${getSentimentColor()}`}>
              {insight.sentiment}
            </span>
          </div>
        </div>

        {/* Confidence Circle */}
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#2a2a2a"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#3742fa"
                strokeWidth="8"
                fill="none"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-terminal-text">
                {insight.confidence}%
              </span>
            </div>
          </div>
          
          <div className="flex-1">
            <p className="text-sm text-terminal-muted mb-2">Confidence Level</p>
            <div className="w-full bg-terminal-border rounded-full h-2">
              <div
                className="bg-neon-blue h-2 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${insight.confidence}%` }}
              ></div>
            </div>
            <p className="text-xs text-terminal-muted mt-1">
              Based on technical indicators & market data
            </p>
          </div>
        </div>
      </div>

      {/* Recommendation Badge */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-terminal-text">Recommendation</span>
          <div className="flex items-center gap-2">
            {getRiskIcon()}
            <span className={`text-sm font-medium ${getRiskColor()}`}>
              {insight.riskLevel} Risk
            </span>
          </div>
        </div>
        
        <div className={`inline-flex items-center px-4 py-2 rounded-lg border font-bold text-lg ${getRecommendationStyle()}`}>
          {insight.recommendation}
        </div>
      </div>

      {/* AI Reasoning */}
      <div>
        <h4 className="text-sm font-medium text-terminal-text mb-3">Analysis Summary</h4>
        <div className="bg-terminal-bg border border-terminal-border rounded-lg p-4">
          <p className="text-sm text-terminal-text leading-relaxed">
            {insight.reasoning}
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-4 pt-4 border-t border-terminal-border">
        <p className="text-xs text-terminal-muted">
          ⚠️ This analysis is for informational purposes only and should not be considered as financial advice. 
          Please consult with a qualified financial advisor before making investment decisions.
        </p>
      </div>
    </div>
  );
};

export default AIInsightPanel;